import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config.js';
import './ProjectsList.css';

/**
 * ProjectsList component displaying projects in a table format
 * @param {Object} props - Component props
 * @param {Array} props.projects - Array of project records
 * @param {function} props.onProjectClick - Callback when a project row is clicked (project)
 * @param {string} props.sortColumn
 * @param {string} props.sortDirection
 * @param {function} props.onSortChange - Called with (column, direction)
 * @returns {JSX.Element} ProjectsList component
 */
const ProjectsList = ({ projects, onProjectClick, sortColumn, sortDirection, onSortChange }) => {
  const { t } = useTranslation();
  const locale = i18n.language === 'no' ? 'no-NO' : 'en-US';

  // Format currency for display
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const statusDotColor = {
    on_track: 'var(--color-lamp-on-track)',
    follow_up: 'var(--color-lamp-follow-up)',
    action_required: 'var(--color-lamp-action-required)',
  };

  // Worst first when ascending, matching the contribution column.
  const statusSortRank = {
    action_required: 0,
    follow_up: 1,
    on_track: 2,
  };

  // Get sort value for a project based on column
  const getSortValue = (project, column) => {
    switch (column) {
      case 'source_system_project_number':
        return (project.source_system_project_number ?? '').toString();
      case 'project_name':
        return project.project_name || '';
      case 'customer_name':
        return project.customer_name || '';
      case 'effective_hourly_rate':
        return project.effective_hourly_rate ?? null;
      case 'contribution':
        return project.contribution_with_non_invoiced_work ?? null;
      case 'remaining_contract_value':
        return project.remaining_contract_value ?? null;
      case 'contract_value_spent_percentage':
        return project.contract_value_spent_percentage ?? null;
      case 'contract_value':
        return project.contract_value ?? null;
      case 'project_performance_category':
        return statusSortRank[project.project_performance_category] ?? statusSortRank.on_track;
      default:
        return null;
    }
  };

  // Sort projects based on current sort column and direction
  const sortProjects = (projectsToSort) => {
    if (!sortColumn) {
      return projectsToSort;
    }

    return [...projectsToSort].sort((a, b) => {
      const aValue = getSortValue(a, sortColumn);
      const bValue = getSortValue(b, sortColumn);

      // Handle null/undefined values - always place them at the end
      if (aValue === null || aValue === undefined) {
        return 1;
      }
      if (bValue === null || bValue === undefined) {
        return -1;
      }

      // Handle empty strings for text columns
      if (sortColumn === 'project_name' || sortColumn === 'customer_name' || sortColumn === 'source_system_project_number') {
        if (aValue === '') return 1;
        if (bValue === '') return -1;
      }

      // Compare values
      let comparison = 0;
      if (aValue < bValue) {
        comparison = -1;
      } else if (aValue > bValue) {
        comparison = 1;
      }

      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      onSortChange(column, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(column, 'asc');
    }
  };

  // Get sort indicator for a column
  const getSortIndicator = (column) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };


  if (!projects || projects.length === 0) {
    return (
      <div className="projects-list-empty">
        <p>{t('projects.noProjects')}</p>
      </div>
    );
  }

  // Sort projects before rendering
  const sortedProjects = sortProjects(projects);

  return (
    <div className="projects-list-container">
      <table className="projects-table">
        <thead>
          <tr>
            <th
              className="sortable-header projects-table-col-project-number"
              onClick={() => handleSort('source_system_project_number')}
            >
              <span className="table-header-content">
                {t('projects.columns.sourceProjectNumber')}
                <span className="sort-indicator">{getSortIndicator('source_system_project_number')}</span>
              </span>
            </th>
            <th
              className="sortable-header projects-table-col-status"
              onClick={() => handleSort('project_performance_category')}
            >
              <span className="table-header-content">
                {t('projects.columns.status')}
                <span className="sort-indicator">{getSortIndicator('project_performance_category')}</span>
              </span>
            </th>
            <th
              className="sortable-header projects-table-col-name"
              onClick={() => handleSort('project_name')}
            >
              <span className="table-header-content">
                {t('projects.columns.name')}
                <span className="sort-indicator">{getSortIndicator('project_name')}</span>
              </span>
            </th>
            <th
              className="sortable-header projects-table-col-customer"
              onClick={() => handleSort('customer_name')}
            >
              <span className="table-header-content">
                {t('projects.columns.customer')}
                <span className="sort-indicator">{getSortIndicator('customer_name')}</span>
              </span>
            </th>
            <th
              className="sortable-header projects-table-col-number"
              onClick={() => handleSort('contribution')}
            >
              <span className="table-header-content">
                {t('projects.columns.profit')}
                <span className="sort-indicator">{getSortIndicator('contribution')}</span>
              </span>
            </th>
            <th
              className="sortable-header projects-table-col-number"
              onClick={() => handleSort('remaining_contract_value')}
            >
              <span className="table-header-content">
                {t('projects.columns.remainingContract')}
                <span className="sort-indicator">{getSortIndicator('remaining_contract_value')}</span>
              </span>
            </th>
            <th
              className="sortable-header projects-table-col-number"
              onClick={() => handleSort('contract_value')}
            >
              <span className="table-header-content">
                {t('projects.columns.contractValue')}
                <span className="sort-indicator">{getSortIndicator('contract_value')}</span>
              </span>
            </th>
            <th className="projects-table-col-chevron" aria-hidden="true"></th>
          </tr>
        </thead>
        <tbody>
          {sortedProjects.map((project) => (
            <tr
              key={project.id}
              className="projects-table-row"
              onClick={() => onProjectClick && onProjectClick(project)}
            >
              <td className="projects-table-col-project-number">{project.source_system_project_number ?? '-'}</td>
              <td className="projects-table-col-status">
                <span
                  className="projects-table-status-dot"
                  style={{
                    background: statusDotColor[project.project_performance_category] || 'var(--color-lamp-on-track)',
                  }}
                  aria-hidden="true"
                />
              </td>
              <td className="projects-table-col-name">
                <span className="cell-line-clamp">{project.project_name || '-'}</span>
              </td>
              <td className="projects-table-col-customer">
                <span className="cell-line-clamp">{project.customer_name || '-'}</span>
              </td>
              <td className="projects-table-col-number">{formatCurrency(project.contribution_with_non_invoiced_work)}</td>
              <td className="projects-table-col-number">{formatCurrency(project.remaining_contract_value)}</td>
              <td className="projects-table-col-number">{formatCurrency(project.contract_value)}</td>
              <td className="projects-table-col-chevron" aria-hidden="true">›</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProjectsList;
